import React, { useContext, useEffect } from 'react';
import { graphql } from 'react-relay';
import { differenceInDays } from 'date-fns';

import { Flex } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';
import { ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceTrackerContext,
	ExperienceStart,
	ExperienceSuccess,
} from '@confluence/experience-tracker/entry-points/component.ts';
import {
	deserializeState,
	useGetOnboardingState,
	useSetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import {
	ONBOARDING_EXPERIENCES,
	NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
} from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { isExpectedApolloError } from '@confluence/error-types/entry-points/expectedGraphQlErrorHelper.ts';
import { UseCasesHomeWelcomeBanner } from '../../entry-points/UseCasesHomeWelcomeBanner.ts';

import { PTV2StoreContainer } from '../usePTV2Store.tsx';

import type { NthUserHomeOnboardingRevampGetUserDataQuery as NthUserHomeOnboardingRevampGetUserDataQuery$data } from './__types__/NthUserHomeOnboardingRevampGetUserDataQuery.ts';
import { NthUserHomeOnboardingActionCards } from './NthUserHomeOnboardingActionCards.tsx';
import { NthUserTemplates } from './NthUserTemplates.tsx';
const GetUserDataQuery = graphql`
	query NthUserHomeOnboardingRevampGetUserDataQuery {
		user: confluence_atlassianUser(current: true) {
			id
			displayName
			team
			confluence {
				hasPersonalSpace
				space {
					id
					key
				}
			}
		}
	}
`;

const SOURCE = 'nth-user-home-onboarding-revamp';

export const NthUserHomeOnboardingRevampContainer = ({
	shouldShowUseCasesHomeWelcomeBanner,
}: {
	shouldShowUseCasesHomeWelcomeBanner?: boolean;
}): React.JSX.Element | null => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { data: onboardingStateData, loading: onboardingStateLoading } = useGetOnboardingState(
		Object.values({
			IS_FOUNDING_ADMIN: ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN,
			...NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS,
		}),
	);

	const {
		data: userData,
		loading: userDataLoading,
		error: userDataError,
	} = useQueryInterop<NthUserHomeOnboardingRevampGetUserDataQuery$data>(GetUserDataQuery);
	const { setOnboardingState } = useSetOnboardingState();

	const { isTemplateListDismissed, takeoverSeenAt, templateUsageValues } =
		deserializeState(onboardingStateData) || {};

	useEffect(() => {
		if (!onboardingStateLoading && !userDataLoading && !takeoverSeenAt) {
			setOnboardingState({
				key: NTH_USER_HOME_ONBOARDING_REVAMP_ONBOARDING_STATE_KEYS.takeoverSeenAt,
				value: new Date().toISOString(),
			});
		}
	}, [onboardingStateLoading, userDataLoading, takeoverSeenAt, setOnboardingState]);

	const personalSpaceKey =
		(userData?.user?.confluence?.hasPersonalSpace && userData?.user?.confluence?.space?.key) || '';

	const teamType = userData?.user?.team || '';

	const displayName = userData?.user?.displayName;

	const onboardingPeriodDay = takeoverSeenAt
		? differenceInDays(new Date(), new Date(takeoverSeenAt))
		: 0;

	const isReadyToRender = !onboardingStateLoading && !userDataLoading && Boolean(personalSpaceKey);

	const personalizationHeuristic = shouldShowUseCasesHomeWelcomeBanner ? 'useCases' : undefined;

	useEffect(() => {
		if (isReadyToRender) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'viewed',
					actionSubject: 'nthUserHomeOnboardingRevamp',
					source: SOURCE,
					attributes: {
						teamType,
						onboardingPeriodDay,
						isTemplateListDismissed: Boolean(isTemplateListDismissed),
						personalizationHeuristic,
					},
				},
			}).fire();
		}
	}, [
		isReadyToRender,
		teamType,
		onboardingPeriodDay,
		isTemplateListDismissed,
		createAnalyticsEvent,
		personalizationHeuristic,
	]);

	if (userDataError) {
		if (isUnauthorizedError(userDataError) || isExpectedApolloError(userDataError)) {
			markErrorAsHandled(userDataError);
			experienceTracker.abort({
				name: ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP,
				reason: 'User is not permitted',
			});
		}

		experienceTracker.stopOnError({
			name: ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP,
			error: userDataError,
		});
		return null;
	}

	if (!isReadyToRender) {
		return null;
	}

	return (
		<PTV2StoreContainer
			teamType={teamType}
			personalSpaceKey={personalSpaceKey}
			isTemplateListDismissed={isTemplateListDismissed}
			onboardingPeriodDay={onboardingPeriodDay}
			personalizationHeuristic={personalizationHeuristic}
			source={SOURCE}
		>
			<Flex direction="column" gap="space.200" data-vc="nth-user-home-onboarding-revamp">
				{shouldShowUseCasesHomeWelcomeBanner ? (
					<UseCasesHomeWelcomeBanner name={displayName ?? ''} />
				) : (
					<NthUserHomeOnboardingActionCards onboardingStateData={onboardingStateData} />
				)}
				<NthUserTemplates
					templateUsageValues={templateUsageValues}
					shouldShowUseCasesHomeWelcomeBanner={shouldShowUseCasesHomeWelcomeBanner}
				/>
			</Flex>
			<ExperienceSuccess name={ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP} />
		</PTV2StoreContainer>
	);
};

export const NthUserHomeOnboardingRevamp = ({
	shouldShowUseCasesHomeWelcomeBanner,
}: {
	shouldShowUseCasesHomeWelcomeBanner?: boolean;
}): React.JSX.Element => {
	const experienceTracker = useContext(ExperienceTrackerContext);
	return (
		<TransparentErrorBoundary
			attribution={Attribution.FLYWHEEL_ENGAGEMENT}
			onError={(error) => {
				experienceTracker.stopOnError({
					name: ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP,
					error,
				});
			}}
		>
			<ExperienceStart name={ONBOARDING_NTH_USER_HOME_ONBOARDING_REVAMP} />
			<NthUserHomeOnboardingRevampContainer
				shouldShowUseCasesHomeWelcomeBanner={shouldShowUseCasesHomeWelcomeBanner}
			/>
		</TransparentErrorBoundary>
	);
};
